import React, { useState } from 'react'
import { Button, Form, Input, InputNumber, message, Select, Upload } from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
import acceptPayment, { AcceptPaymentBody } from 'api/payment/acceptPayment'
import handleFormError from 'utils/handleFormError'
import { UploadOutlined } from '@ant-design/icons'
import { AttachmentType } from 'types'
import upload, { FileUpload } from 'api/file/upload'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import getFileNameFromURL from 'utils/getFileNameFromURL'
import handleAxiosError from 'utils/handleAxiosError'

const { Item } = Form
const { Option } = Select

interface PropTypes {
	invoiceId: string
	onSuccess: Function
}

const AcceptPaymentForm: React.FC<PropTypes> = ({ invoiceId, onSuccess }) => {
	const [loading, setLoading] = useState<boolean>(false)
	const [sampleAttachment, setSampleAttachment] =
		useState<AttachmentType | null>(null)
	const [uploadError, setUploadError] = useState(false)

	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			const { url } = await upload(options)
			const { file } = options as any
			setSampleAttachment({
				link: url,
				fileName: file?.name || getFileNameFromURL(url),
			})
		} catch (error: any) {
			handleAxiosError(error)
		}
	}

	const beforeUpload = (file: File) => {
		const isPDF = file.type === 'application/pdf'
		if (!isPDF) {
			message.error(`Only PDF file accepted`)
			setUploadError(true)
		}
		setUploadError(false)
		return isPDF || Upload.LIST_IGNORE
	}

	const onFinish = async (values: AcceptPaymentBody) => {
		const body = {
			amount: values.amount,
			remarks: values.remarks,
			paymentId: values.paymentId,
			txnChannel: values.txnChannel,
			url: sampleAttachment?.link,
		}
		try {
			setLoading(true)
			await acceptPayment(invoiceId, body)
			setLoading(false)
			message.success('Invoce created.')
			onSuccess(true)
		} catch (error: any) {
			setLoading(false)
			onSuccess(false)
		}
	}

	const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
		handleFormError(errorInfo)
	}

	return (
		<Form
			name='aceptPayment'
			labelCol={{ span: 20 }}
			wrapperCol={{ span: 20 }}
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			autoComplete='off'
			layout='vertical'
			initialValues={{
				amount: '',
				remarks: '',
				txnChannel: '',
				url: '',
				paymentId: '',
			}}
		>
			<Item
				label='Amount'
				name='amount'
				rules={[{ required: true, message: 'Please enter amount!' }]}
			>
				<InputNumber style={{ width: '100%' }} min={1} />
			</Item>
			<Item
				label='Transaction Channel'
				name='txnChannel'
				rules={[{ required: true, message: 'Please enter transaction channel!' }]}
			>
				<Select placeholder='Select'>
					<Option value='PAYTM'>PAYTM</Option>
					<Option value='UPI'>UPI</Option>
					<Option value='RAZORPAY'>RAZORPAY</Option>
					<Option value='OFFLINE'>OFFLINE</Option>
					<Option value='RTGS/NEFT'>RTGS/NEFT</Option>
					<Option value='CHEQUE'>CHEQUE</Option>
					<Option value='BANK_TRANSFER'>BANK_TRANSFER</Option>
				</Select>
			</Item>
			<Item
				label='Remarks'
				name='remarks'
				rules={[{ required: true, message: 'Please enter remarks!' }]}
			>
				<Input />
			</Item>
			<Item label='Upload File' name='url'>
				<Upload
					maxCount={1}
					onRemove={() => setSampleAttachment(null)}
					beforeUpload={beforeUpload}
					showUploadList={!uploadError}
					multiple={false}
					customRequest={uploadFile}
					defaultFileList={[
						...(sampleAttachment !== null
							? [
									{
										uid: '1',
										name:
											sampleAttachment.fileName ||
											getFileNameFromURL(sampleAttachment.link),
										url: sampleAttachment.link,
									},
							  ]
							: []),
					]}
					accept='APPLICATION/PDF'
				>
					<Button icon={<UploadOutlined />}>Upload a PDF File</Button>
				</Upload>
			</Item>
			<Item
				label='Payment ID'
				name='paymentId'
				rules={[{ required: true, message: 'Please enter Payment ID!' }]}
			>
				<Input />
			</Item>
			<Item>
				<Button type='primary' htmlType='submit' loading={loading}>
					Submit
				</Button>
			</Item>
		</Form>
	)
}

export default AcceptPaymentForm
